import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { BehaviorSubject, Observable, Subscription, of } from "rxjs";
import { catchError, finalize, map, share, shareReplay, tap } from "rxjs/operators";
import { REQUEST_TYPE } from "src/app/constants";
import { environment } from "src/environments/environment";
import { BaseModel } from "./crud-table/models/base.model";
import { GroupingState } from "./crud-table/models/grouping.model";
import { PaginatorState } from "./crud-table/models/paginator.model";
import { SortState } from "./crud-table/models/sort.model";
import {
	ITableState,
	TableResponseModel,
} from "./crud-table/models/table.model";

const DEFAULT_STATE: ITableState = {
	filter: {},
	paginator: new PaginatorState(),
	sorting: new SortState(),
	searchTerm: "",
	grouping: new GroupingState(),
	// eslint-disable-next-line no-undefined
	entityId: undefined
};

export abstract class BaseService<T> {
	// Private fields
	protected _items$ = new BehaviorSubject<T[]>([]);
	protected _item$ = new BehaviorSubject<T>(null);
	protected _totalItems$ = new BehaviorSubject<number>(0);
	protected _isLoading$ = new BehaviorSubject<boolean>(false);
	protected _isScrollUpLoading$ = new BehaviorSubject<boolean>(false);
	protected _tableState$ = new BehaviorSubject<ITableState>(DEFAULT_STATE);
	protected _errorMessage = new BehaviorSubject<string>("");
	protected _orders$ = new BehaviorSubject( [] );
	protected _subscriptions: Subscription[] = [];
	protected _spinnerService: NgxSpinnerService;

	// Getters
	get items$() {
		return this._items$.asObservable();
	}
	set items$(items: any) {
		this._items$.next(items);
	}
	get item$() {
		return this._item$.asObservable();
	}
	get isLoading$() {
		return this._isLoading$.asObservable();
	}
	set isLoading$(isLoad: any) {
		this._isLoading$.next(isLoad);
	}
	get errorMessage$() {
		return this._errorMessage.asObservable();
	}
	get subscriptions() {
		return this._subscriptions;
	}
	// State getters
	get paginator() {
		return this._tableState$.value.paginator;
	}
	get filter() {
		return this._tableState$.value.filter;
	}
	get sorting() {
		return this._tableState$.value.sorting;
	}
	get searchTerm() {
		return this._tableState$.value.searchTerm;
	}
	get grouping() {
		return this._tableState$.value.grouping;
	}

	get orders$() {
		return this._orders$.asObservable();
	}

	set tableState(tableState: ITableState) {
		this._tableState$.next(tableState);
	}

	get tableState() {
		return this._tableState$.value;
	}

	get tableState$() {
		return this._tableState$.asObservable();
	}

	get totalItems$() {
		return this._totalItems$.asObservable();
	}

	get isScrollUpLoading$() {
		return this._isScrollUpLoading$.asObservable();
	}

	protected http: HttpClient;
	protected API_URL = `${environment.API_ENDPOINT}`;
	protected VERSION_V1 = `${environment.VERSION_V1}`;
	protected API_ROUTES = environment.API_ROUTES;

	constructor(http: HttpClient) {
		this.http = http;
		this._spinnerService = new NgxSpinnerService();
	}

	/**
	 * Handle create item
	 * @param item : Data object for item create
	 * @returns
	 */
	create(item: T, url: string, language?: string): Observable<any> {
		this._errorMessage.next("");
		this._spinnerService.show();

		const headers = new HttpHeaders().set('x-language',language);

		return this.http.post<Observable<any>>(url, item, (language ? {'headers': headers, observe: "response"} : {observe: "response"})).pipe(
			catchError((err) => {
				this._errorMessage.next(err);
				console.error("CREATE ITEM", err);
				this._errorMessage.next(err.error.message);
				throw { error: err };
			}),
			finalize(() => {
				this._spinnerService.hide();
				this._isLoading$.next(false);
			})
		);
	}

	/**
	 * Handle item update
	 * @param: endpoint : rest url
	 * @param item : Updated data object
	 * @returns
	 */
	put(endpoint: string, payload, language?: string): Observable<any> {
		this._errorMessage.next("");
		this._spinnerService.show();
		this._isLoading$.next(true);
		const headers = new HttpHeaders().set('x-language',language);
		return this.http.put(endpoint, payload, (language ? {'headers': headers, observe: "response"} : {observe: "response"})).pipe(
			shareReplay(),
			catchError((err) => {
				this._errorMessage.next(err);
				this._errorMessage.next(err.error.message);
				console.error("UPDATE ITEM", payload, err);
				return of({ error: err });
			}),
			finalize(() => {
				this._spinnerService.hide();
				this._isLoading$.next(false);
			})
		);
	}


		/**
	 * Handle item update
	 * @param: endpoint : rest url
	 * @param item : Updated data object
	 * @returns
	 */
		update(endpoint: string, payload, language?: string): Observable<any> {
			this._errorMessage.next("");
			this._spinnerService.show();
			this._isLoading$.next(true);
			const headers = new HttpHeaders().set('x-language',language);
			return this.http.put(endpoint, payload,{reportProgress: true,observe: "events"}).pipe(
				shareReplay(),
				catchError((err) => {
					this._errorMessage.next(err);
					this._errorMessage.next(err.error.message);
					console.error("UPDATE ITEM", payload, err);
					return of({ error: err });
				}),
				finalize(() => {
					this._spinnerService.hide();
					// this._isLoading$.next(false);
				})
			);
		}

		createDraft( url: string, language?: string): Observable<any> {
			this._errorMessage.next("");
			this._spinnerService.show();

			const headers = new HttpHeaders().set('x-language',language);

			return this.http.post<Observable<any>>(url, {data:{}}, (language ? {'headers': headers, observe: "response"} : {observe: "response"})).pipe(
				catchError((err) => {
					this._errorMessage.next(err);
					console.error("CREATE DRAFT", err);
					this._errorMessage.next(err.error.message);
					throw { error: err };
				}),
				finalize(() => {
					this._spinnerService.hide();
					this._isLoading$.next(false);
				})
			);
		}

	getTenderDetails(url, tenderId) {
		this._errorMessage.next("");
		this._isLoading$.next(true);
		this._spinnerService.show();
		return this.http.get<T>(url, tenderId).pipe(
			tap((res) => {}),
			catchError((err) => {
				this._errorMessage.next(err);
				return of({ id: undefined });
			}),
			shareReplay(),
			finalize(() => {
				this._spinnerService.hide();
				this._isLoading$.next(false);
			})
		)}

	/**
	 *	Handle post requests
	 * @param endpoint :current url
	 * @param tableState :current table state
	 * @returns
	 */
	public post(endpoint: string, payload, language?: string): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		this._isLoading$.next(true);
		const headers = new HttpHeaders().set('x-language',language);
		// return this.http.post<TableResponseModel<T>>(endpoint, payload);
		return this.http.post<TableResponseModel<T>>(endpoint, payload, (language ? {'headers': headers} : {})).pipe(
			tap((res: TableResponseModel<T>) => {
				this._items$.next(res.items);
			}),
			catchError(err => {
				this._errorMessage.next(err);
				console.error("FIND ITEMS", err);
				return of({ items: [], total: 0 });
			}),
			finalize(() => {
				this._spinnerService.hide();
				this._isLoading$.next(false);
				this._isScrollUpLoading$.next(false);
			})
		);
	}

	public publishDraftTender(endpoint: string, payload): Observable<any> {
		this._errorMessage.next("");
		this._spinnerService.show();
		this._isLoading$.next(true);
		return this.http.post<any>(endpoint, payload).pipe(
			tap((res: any) => {
			}),
			catchError(err => {
				this._errorMessage.next(err);
				console.error("publishDraftTender", err);
				this._spinnerService.hide();
				throw { error: err };
			}),
			finalize(() => {
				this._spinnerService.hide();
			})
		);
	}


	/**
	 * Delete list of items by ids
	 */
	delete(
		endpoint: string,
		bodyData: object
	): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http
			.request(REQUEST_TYPE.DELETE, endpoint, { body: bodyData })
			.pipe(
				catchError((err) => {
					this._errorMessage.next(err);
					console.error("DELETE SELECTED ITEMS", bodyData, err);
					return of(err);
				}),
				finalize(() => {
					this._spinnerService.hide();
					this._items$ = new BehaviorSubject<T[]>([]);
				})
			);
	}

	/**
	 * Get item by its Id
	 */
	getById(endpoint: string, id: string) {
		this._errorMessage.next("");
		this._isLoading$.next(true);
		this._spinnerService.show();
		return this.http.get<T>(endpoint).pipe(
			tap((res: T) => {
				this._item$.next(res);
			}),
			catchError((err) => {
				this._errorMessage.next(err);
				console.error("GET ITEM BY IT", id, err);
				// eslint-disable-next-line no-undefined
				return of({ id: undefined });
			}),
			shareReplay(),
			finalize(() => {
				this._spinnerService.hide();
				this._isLoading$.next(false);
			})
		);
	}

	/**
	 * Handle data fetch for given API endpoint
	 * @param endpoint : string API endpoint
	 */
	public fetch(endpoint: string):Observable<TableResponseModel<T>> {

		const request = this.post(endpoint, this.tableState)
			.pipe(
				share(),
				shareReplay(),
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
					this._totalItems$.next(res.total);
					this._isLoading$.next(false);
					this.patchStateWithoutFetch({
						paginator:
							this._tableState$.value.paginator.recalculatePaginator(
								res.total
							),
					});
				}),
				finalize(() => {
					const itemIds = this._items$.value.map((el: T) => {
						const item = el as unknown as BaseModel;
						return item.id;
					});
					this._isLoading$.next(false);
					this.patchStateWithoutFetch({
						grouping:
							this._tableState$.value.grouping.clearRows(itemIds),
					});
					this._spinnerService.hide();
				})
			)
			// .subscribe();
			return request;
		// this._subscriptions.push(request);
	}

	public patchState(endpoint, patch: Partial<ITableState>): Observable<any> {
		this.patchStateWithoutFetch(patch);
		return this.fetch(endpoint);
	}

	public patchStateWithoutFetch(patch: Partial<ITableState>) {
		const newState = Object.assign(this._tableState$.value || {}, patch);
		this._tableState$.next(newState);
	}

	/**
	 * Handle fetch categories
	 * @param endpoint : url endpoint for finding categories
	 */
	 findItemCategories(endpoint: string, tableState = {}, language?: string): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();

		const headers = new HttpHeaders().set('x-language',language);
		return this.http.post<TableResponseModel<T>>(endpoint, tableState, (language ? {'headers': headers} : {})).pipe(
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND CATEGORY", err);
					return of({ categories: [], total: 0 });
				}),
				finalize(() => this._spinnerService.hide())
			);
	}

	/**
	 * Download user's products
	 * @param endpoint : url endpoint for downloading product
	 * @param body : ids of products that need to be downloaded
	 */
	 downloadItems(endpoint: string, body?, language?: string) {
		this._errorMessage.next("");
		this._spinnerService.show();
		this._isLoading$.next(true);
		const headers = new HttpHeaders().set('x-language',language);
		if(body) {
			return this.http.request(REQUEST_TYPE.POST, endpoint, { responseType: "blob", body, 'headers': headers })
			.pipe(
				catchError(err => {
					this._errorMessage.next(err);
					console.error("DOWNLOAD ITEMS", err);
					throw err;
				}),
				finalize(() => {
					this._spinnerService.hide();
					this._isLoading$.next(false);
				})
			);
		} else {
			return this.http.request(REQUEST_TYPE.GET, endpoint, { responseType: "blob" })
			.pipe(
				catchError(err => {
					this._errorMessage.next(err);
					console.error("DOWNLOAD ITEMS", err);
					throw err;
				}),
				finalize(() => {
					this._spinnerService.hide();
					this._isLoading$.next(false);
				})
			);
		}
	}

	/**
	 * Handle find items
	 * @param endpoint : url endpoint for finding items
	 */
	 findItems(endpoint: string, tableState: ITableState): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.post<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._totalItems$.next(res.total);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				}),
				finalize(() => this._spinnerService.hide())
			);
	}

	getDetails(endpoint: string) {
		this._errorMessage.next("");
		this._spinnerService.show();
		this._isLoading$.next(true);
		return this.http.get<any>(endpoint).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("GET ITEM BY IT", err);
				throw err;
			}),
			shareReplay(),
			finalize(() => {
				this._spinnerService.hide();
				this._isLoading$.next(false);
			})
		);
	}

	getItems(endpoint: string, tableState = {}): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.get<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				})
			);
		this._items$.next(data["items"]);
		this._spinnerService.hide();
		return data;
	}

	/**
	 * Delete list of items by ids
	 */
	 deleteItems(bodyData: object, url: string): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.request(REQUEST_TYPE.DELETE, url, { body: bodyData }).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("DELETE SELECTED ITEMS", bodyData, err);
				return of(err);
			}),
			finalize(() => {
				this._spinnerService.hide();
				this._items$ = new BehaviorSubject<T[]>([]);
			})
		);
	}

	/**
	 * Delete list of item by id
	 */
	 deleteItem(url: string): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.delete(url).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("DELETE SELECTED ITEMS", err);
				return of(null);
			}),
			finalize(() => this._spinnerService.hide())
		);
	}

	findUser(endpoint: string, tableState = {}): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.get<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				})
			);
		this._items$.next(data["items"]);
		this._spinnerService.hide();
		return data;
	}

	/**
	 *	Handle find event on for table
	 * @param tableState :current table state
	 * @returns
	 */
	findAddress(endpoint: string, tableState: ITableState): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.post<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
					this._totalItems$.next(res.total);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				}),
				finalize(() => {
					const itemIds = this._items$.value.map((el: T) => {
						const item = (el as unknown) as BaseModel;
						return item.id;
					});
					this.patchStateWithoutFetch({
						// grouping: this._tableState$.value.grouping.clearRows( itemIds )
					});
					this.grouping.itemIds = [...this.grouping.itemIds, ...itemIds];
					this._spinnerService.hide();
				})
			);
		return data;
	}

	/**
	 *
	 * @param endpoint : quotation award endpoint
	 */
	awardQuotations(endpoint: string,language) {
		this._errorMessage.next("");
		this._spinnerService.show();
		const headers= new HttpHeaders()
		.set('x-language',language);

		const data = this.http.get<TableResponseModel<T>>(endpoint,{ 'headers': headers })
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next([]);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of(err.error);
				})
			);
		this._spinnerService.hide();
		return data;
	}

	findCompareQuotations(endpoint: string) {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.get<TableResponseModel<T>>(endpoint)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				})
			);
		this._items$.next(data["items"]);
		this._spinnerService.hide();
		return data;
	}
}
